/**
 * Single source of truth for tearing down a user session.
 *
 * Three call sites:
 *   - User-initiated logout button (App.tsx handleLogout)
 *   - axios response interceptor on HTTP 401 (farmvest_api.ts)
 *   - Forced re-login when MLE keys can't be restored (farmvest_api.ts)
 *
 * Anything tied to user identity — bearer session, MLE key material in IDB +
 * memory, cached per-user UI state — must be wiped here so the three paths
 * stay in lockstep.
 *
 * NOT cleared: cross-user UI preferences (`activeTab`, `orders_*` filters,
 * `privacy_policy_content_v1`). Keep those across sessions intentionally.
 *
 * Callers are responsible for their own post-clear actions (Redux dispatch,
 * navigation, React state reset).
 */

import { mleService } from './mleService';

const PER_USER_LOCAL_STORAGE_KEYS = [
  'ak_dashboard_session',
  'ak_dashboard_last_mobile',
  'mle_enabled',
  'fv_selected_farm_id',
  'farmvest_payments',
] as const;

export async function clearSession(): Promise<void> {
  // Await so the IndexedDB `mle-store` clear commits before the caller
  // navigates / setSession(null), preventing a race with the fresh
  // initialize() that runs on Login mount.
  await mleService.clearKeys();

  for (const key of PER_USER_LOCAL_STORAGE_KEYS) {
    window.localStorage.removeItem(key);
  }

  // `dynamic_admin_mobile` (cached in getAdminMobile) and anything else
  // ephemeral lives here. Nothing in sessionStorage should survive logout.
  window.sessionStorage.clear();
}
